import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Sparkles } from 'lucide-react';
import { plansAPI } from '../api/client';
import { traceError } from '../lib/trace';
import { euros, planFeatures } from '../lib/plans';
import { HelpLink } from '../components/layout/HelpLink';

export function Plans() {
  const queryClient = useQueryClient();
  const [annual, setAnnual] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { data: catalogue, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: plansAPI.list,
  });
  const { data: current } = useQuery({ queryKey: ['plan'], queryFn: plansAPI.current });

  const changePlan = useMutation({
    mutationFn: (plan: string) => plansAPI.change(plan),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['plan'] });
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (e: unknown) => {
      // Downgrades are refused when usage exceeds the target plan; the server
      // says exactly what has to go first, so surface it verbatim.
      const message = e instanceof Error ? e.message : String(e);
      traceError('plans', 'changing plan failed', { error: e });
      setError(message);
    },
  });

  if (isLoading) {
    return <div className="loading-center" />;
  }

  const plans = catalogue?.plans ?? [];

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <h1 className="page-title">Plans</h1>
        <HelpLink topic="plans" label="Plans" />
      </div>
          <p className="page-subtitle">
            {current
              ? `You are on ${current.plan.name} — ${current.usage.monitors} monitor(s), ${current.usage.seats} seat(s) in use.`
              : 'Choose the plan that fits your monitoring.'}
          </p>
        </div>

        <div className="billing-toggle" role="group" aria-label="Billing period">
          <button
            type="button"
            className={`billing-toggle-option${annual ? ' is-selected' : ''}`}
            onClick={() => setAnnual(true)}
          >
            Annual
          </button>
          <button
            type="button"
            className={`billing-toggle-option${annual ? '' : ' is-selected'}`}
            onClick={() => setAnnual(false)}
          >
            Monthly
          </button>
        </div>
      </div>

      <p className="plans-note">
        <Sparkles size={14} aria-hidden="true" />
        Plans are free while billing is being set up — pick any tier and it applies immediately.
      </p>

      {error && (
        <div className="plans-error" role="alert">
          {error}
        </div>
      )}

      <div className="plan-grid">
        {plans.map(plan => {
          const isCurrent = current?.plan.id === plan.id;
          const price = annual ? plan.price_annual_monthly_cents : plan.price_monthly_cents;

          return (
            <div key={plan.id} className={`plan-card${plan.highlighted ? ' is-highlighted' : ''}`}>
              {plan.highlighted && <span className="plan-badge">Most popular</span>}

              <h2 className="plan-name">{plan.name}</h2>

              <div className="plan-price">
                {plan.custom_pricing ? (
                  <span className="plan-price-custom">Let’s talk</span>
                ) : (
                  <>
                    {annual && plan.price_monthly_cents > plan.price_annual_monthly_cents && (
                      <span className="plan-price-was">{euros(plan.price_monthly_cents)}</span>
                    )}
                    <span className="plan-price-now">{euros(price)}</span>
                    <span className="plan-price-period">/month</span>
                  </>
                )}
              </div>

              <p className="plan-description">{plan.description}</p>

              {plan.self_serve ? (
                <button
                  type="button"
                  className={`btn ${isCurrent ? 'btn-secondary' : 'btn-primary'} plan-cta`}
                  disabled={isCurrent || changePlan.isPending || current?.is_owner === false}
                  onClick={() => changePlan.mutate(plan.id)}
                >
                  {isCurrent ? 'Current plan' : `Switch to ${plan.name}`}
                </button>
              ) : (
                <a className="btn btn-secondary plan-cta" href="mailto:sales@uptimer.tsew.com">
                  Book a demo
                </a>
              )}

              <ul className="plan-features">
                {planFeatures(plan).map(feature => (
                  <li key={feature}>
                    <Check size={14} aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {current?.is_owner === false && (
        <p className="plans-note">Only the team owner can change the plan.</p>
      )}
    </div>
  );
}
